import * as React from 'react';
import { withTranslation } from 'react-i18next';
import { Field, BaseFieldProps } from 'redux-form';

import { Select } from '@common/components/form/inputs/select';
import Icon, { IconTypes } from '../../../../common/components/icon';
import { EPrivacyLevels } from '../../../../common/definitions';
import './privacy-select.scss';

type Props = {
  value: string | boolean,
  onChange: (value: string) => void,
  onlyBoolean?: boolean,
  width?: number | string,
  t: (key: string, values?: Object) => string,
};

type TOption = {
  icon: string;
  value: string;
  label: string;
  description: string;
};

const valueComponent = (
  {
    value: option,
  }: {
    value: Record<string, any>;
  }
) => (
  <div className="Select-value">
    <span className="Select-value-label">
      <Icon type={option.icon as IconTypes} />
      {option.label}
    </span>
  </div>
);


const optionComponent = (
  {
    option,
    isSelected,
    onSelect,
  }: {
    option: TOption;
    isSelected?: boolean;
    onSelect?: (value: string, event?: any) => void;
  }
) => {
  const classNames = ['Select-option PrivacySelectOption'];
  if (isSelected) classNames.push('is-selected');

  return (
    <div
      className={classNames.join(' ')}
      onClick={!isSelected && onSelect ? () => onSelect(option.value) : undefined}
    >
      {option.icon && <Icon type={option.icon as IconTypes} isFilled={isSelected} />}
      <div className="PrivacySelectOption__Text">
        <div className="PrivacySelectOption__Label">
          {option.label}
        </div>
        <small className="PrivacySelectOption__Description">
          {option.description}
        </small>
      </div>
    </div>
  );
};

const PrivacySelectComponent = (
  {
    value,
    onChange,
    onlyBoolean,
    width,
    t,
  }: Props
) => {
  const options: Array<Object> = [
    {
      value: EPrivacyLevels.ORGANISATION,
      icon: 'group',
      label: t('core:privacy_option_everyone'),
      description: t('core:privacy_option_everyone_description'),
    },
    !onlyBoolean && {
      value: EPrivacyLevels.NETWORK,
      icon: 'business',
      label: t('core:privacy_option_network'),
      description: t('core:privacy_option_network_description'),
    },
    {
      value: EPrivacyLevels.NOBODY,
      icon: 'lock',
      label: t('core:privacy_option_no_one'),
      description: t('core:privacy_option_no_one_description'),
    },
  ].filter((o) => !!o);

  // Only apply style if width prop is provided, otherwise use default from CSS
  const containerStyle = width ? { width } : undefined;

  return (
    <div className="privacy-select-container" style={containerStyle}>
      <Select
        className="PrivacySelect"
        clearable={false}
        value={value}
        // @ts-expect-error
        onChange={onChange}
        options={options}
        valueComponent={valueComponent}
        // @ts-expect-error
        optionComponent={optionComponent}
      />
    </div>
  );
};

const WrappedPrivacySelect = withTranslation()(PrivacySelectComponent);

type FieldProps = {
  input: {
    value: string,
    onChange: (newValue: string) => void,
  },
  onlyBoolean?: boolean,
  width?: number | string,
};

const ForField = ({ input, onlyBoolean, width }: FieldProps) => (
  <WrappedPrivacySelect value={input.value} onChange={input.onChange} onlyBoolean={onlyBoolean} width={width} />
);

export const PrivacyFieldSelect = (props: Omit<BaseFieldProps<Omit<FieldProps, 'input'>>, 'component'>) => (
  <Field {...props} component={ForField} />
);

export default WrappedPrivacySelect;
